// src/services/cnpj-external/cnpjws/adapter.js
// *******************************************************************************
// CNPJ.ws Adapter
// *******************************************************************************
// Provider: CNPJ.ws (https://www.cnpj.ws)
// Endpoint: GET https://publica.cnpj.ws/cnpj/{cnpj}
// Rate Limit: 3 requests/minute (free tier)
// Documentation: https://www.cnpj.ws/docs/api-publica
// *******************************************************************************

import fetchX from 'utils/fetchX';
import { normalize } from 'utils/helpers';
import {
  createEmptyCnpjPojo,
  createFailureCnpjPojo,
  mapRegistrationStatus,
  parseBrazilianDate,
  cleanPhoneNumber,
  derivePhoneType,
  cleanCnpj,
  cleanCep,
  getIsicSectionFromCnae,
} from '../cnpjUniversalPojo.js';

// Helper to normalize text to Capital Case (defensive: handles null/undefined/non-string)
const toCapital = str => {
  if (str == null) return null;
  if (typeof str !== 'string') return String(str);
  return normalize(str, 'capital');
};

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PROVIDER_NAME = 'cnpjws';
const BASE_URL = 'https://publica.cnpj.ws/cnpj';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Searches CNPJ data via CNPJ.ws
 *
 * @param {Object} payload - Search parameters
 * @param {string} payload.cnpj - CNPJ number (with or without formatting)
 * @returns {Promise<Object>} Raw API response
 */
async function searchCnpj(payload) {
  const { cnpj } = normalizeRequestPayload(payload);

  const url = `${BASE_URL}/${cnpj}`;

  try {
    const response = await fetchX(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      // CNPJ.ws returns 404 for not found, 400 for invalid
      if (response.status === 404) {
        return {
          _apiWorked: true,
          _notFound: true,
          _error: 'CNPJ not found',
        };
      }

      if (response.status === 400) {
        return {
          _apiWorked: true,
          _error: 'Invalid CNPJ format',
        };
      }

      // Rate limit exceeded (429)
      if (response.status === 429) {
        return {
          _apiWorked: false,
          _error: 'Rate limit exceeded. Try again in 1 minute.',
        };
      }

      return {
        _apiWorked: false,
        _error: `API error: ${response.status} ${response.statusText}`,
      };
    }

    const data = await response.json();
    return {
      ...data,
      _apiWorked: true,
    };
  } catch (error) {
    return {
      _apiWorked: false,
      _error: error.message || 'Network error',
    };
  }
}

/**
 * Normalizes incoming payload
 *
 * @param {Object} payload - Raw payload
 * @returns {Object} Normalized payload
 */
function normalizeRequestPayload(payload) {
  const { cnpj } = payload;
  return {
    cnpj: cleanCnpj(cnpj),
  };
}

/**
 * Normalizes CNPJ.ws response to Universal POJO
 *
 * CNPJ.ws response structure:
 * {
 *   cnpj_raiz: "00000000",
 *   razao_social: "BANCO DO BRASIL SA",
 *   capital_social: "120000000000.00",
 *   responsavel_federativo: "",
 *   atualizado_em: "2024-01-01T00:00:00.000Z",
 *   porte: { id: "05", descricao: "DEMAIS" },
 *   natureza_juridica: { id: "2038", descricao: "Sociedade de Economia Mista" },
 *   qualificacao_do_responsavel: { id: "05", descricao: "ADMINISTRADOR" },
 *   socios: [
 *     {
 *       cpf_cnpj_socio: "***123456**",
 *       nome: "FULANO DE TAL",
 *       tipo: "Pessoa Física",
 *       data_entrada: "2020-01-01",
 *       cpf_representante_legal: "***654321**",
 *       nome_representante: "BELTRANO",
 *       faixa_etaria: "Entre 41 a 50 anos",
 *       atualizado_em: "2024-01-01T00:00:00.000Z",
 *       pais_id: "BR",
 *       qualificacao_socio: { id: "10", descricao: "DIRETOR" },
 *       qualificacao_representante: { id: "05", descricao: "ADMINISTRADOR" }
 *     }
 *   ],
 *   simples: {
 *     simples: "Sim"/"Não",
 *     data_opcao_simples: "2000-01-01",
 *     data_exclusao_simples: null,
 *     mei: "Sim"/"Não",
 *     data_opcao_mei: null,
 *     data_exclusao_mei: null,
 *     atualizado_em: "2024-01-01T00:00:00.000Z"
 *   },
 *   estabelecimento: {
 *     cnpj: "00000000000191",
 *     tipo: "Matriz",
 *     nome_fantasia: "BANCO DO BRASIL",
 *     situacao_cadastral: "Ativa",
 *     data_situacao_cadastral: "2005-11-03",
 *     data_inicio_atividade: "1966-08-01",
 *     nome_cidade_exterior: null,
 *     tipo_logradouro: "QUADRA",
 *     logradouro: "SAUN Q 5 LOTE B TORRES I,II E III",
 *     numero: "SN",
 *     complemento: "",
 *     bairro: "ASA NORTE",
 *     cep: "70073901",
 *     ddd1: "61",
 *     telefone1: "32161000",
 *     ddd2: null,
 *     telefone2: null,
 *     ddd_fax: null,
 *     fax: null,
 *     email: "contato@bb.com.br",
 *     motivo_situacao_cadastral: null,
 *     situacao_especial: null,
 *     data_situacao_especial: null,
 *     atualizado_em: "2024-01-01T00:00:00.000Z",
 *     atividade_principal: { id: "6422100", descricao: "Bancos múltiplos, com carteira comercial" },
 *     atividades_secundarias: [{ id: "6493000", descricao: "..." }],
 *     pais: { id: "BR", iso2: "BR", iso3: "BRA", nome: "Brasil" },
 *     estado: { id: 53, nome: "Distrito Federal", sigla: "DF", ibge_id: 53 },
 *     cidade: { id: 5300108, nome: "Brasília", ibge_id: 5300108, siafi_id: "9701" },
 *     inscricoes_estaduais: []
 *   }
 * }
 *
 * @param {Object} response - CNPJ.ws response
 * @returns {CnpjUniversalPojo} Universal POJO
 */
function normalizeResponseBody(response) {
  // Handle API failure
  if (!response._apiWorked) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: false,
      error: response._error || 'API request failed',
    });
  }

  // Handle CNPJ not found
  if (response._notFound) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: true,
      error: 'CNPJ not found in Receita Federal database',
    });
  }

  // Handle other errors
  if (response._error) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: true,
      error: response._error,
    });
  }

  // Extract nested objects
  const estabelecimento = response.estabelecimento || {};
  const simples = response.simples || {};
  const socios = response.socios || [];

  // Build Universal POJO from successful response
  const pojo = createEmptyCnpjPojo();

  // Provider metadata
  pojo.provider = PROVIDER_NAME;
  pojo.success = true;
  pojo.apiWorked = true;
  pojo.error = null;
  pojo.source = PROVIDER_NAME;

  // Company names
  pojo.name = toCapital(estabelecimento.nome_fantasia || response.razao_social) || null;
  pojo.fullName = toCapital(response.razao_social) || null;

  // Dates
  pojo.foundedAt = parseBrazilianDate(estabelecimento.data_inicio_atividade);

  // Documents - CNPJ
  pojo.documents = [
    {
      documentType: 'cnpj',
      value: cleanCnpj(estabelecimento.cnpj),
      countryCode: 'BR',
      isPrimary: true,
    },
  ];

  // Contacts - Phones and Email
  // cleanPhoneNumber fixes old mobile numbers missing the "9" prefix
  // derivePhoneType determines the correct type from the corrected number
  pojo.contacts = [];

  // Phone 1
  if (estabelecimento.ddd1 && estabelecimento.telefone1) {
    const cleaned1 = cleanPhoneNumber(`${estabelecimento.ddd1}${estabelecimento.telefone1}`);
    pojo.contacts.push({
      contactType: 'phone',
      phoneType: derivePhoneType(cleaned1),
      value: cleaned1,
      countryCode: 'BR',
      isPrimary: true,
    });
  }

  // Phone 2
  if (estabelecimento.ddd2 && estabelecimento.telefone2) {
    const cleaned2 = cleanPhoneNumber(`${estabelecimento.ddd2}${estabelecimento.telefone2}`);
    pojo.contacts.push({
      contactType: 'phone',
      phoneType: derivePhoneType(cleaned2),
      value: cleaned2,
      countryCode: 'BR',
      isPrimary: false,
    });
  }

  // Fax
  if (estabelecimento.ddd_fax && estabelecimento.fax) {
    pojo.contacts.push({
      contactType: 'fax',
      phoneType: 'fax',
      value: cleanPhoneNumber(`${estabelecimento.ddd_fax}${estabelecimento.fax}`),
      countryCode: 'BR',
      isPrimary: false,
    });
  }

  // Email
  if (estabelecimento.email) {
    pojo.contacts.push({
      contactType: 'email',
      value: estabelecimento.email.toLowerCase(),
      isPrimary: true,
    });
  }

  // Addresses
  const streetName = estabelecimento.tipo_logradouro
    ? `${estabelecimento.tipo_logradouro} ${estabelecimento.logradouro || ''}`.trim()
    : estabelecimento.logradouro || '';

  pojo.addresses = [
    {
      addressType: 'main',
      zip: cleanCep(estabelecimento.cep),
      address1: toCapital(streetName) || '',
      address2: toCapital(estabelecimento.complemento) || '',
      number: estabelecimento.numero || '',
      neighborhood: toCapital(estabelecimento.bairro) || '',
      city: toCapital(estabelecimento.cidade?.nome) || '',
      cityCode: estabelecimento.cidade?.ibge_id ? String(estabelecimento.cidade.ibge_id) : '',
      region: estabelecimento.estado?.sigla || '',
      countryCode: estabelecimento.pais?.iso2 || 'BR',
      isPrimary: true,
    },
  ];

  // Activities - Main CNAE
  pojo.activities = [];
  if (estabelecimento.atividade_principal?.id) {
    pojo.activities.push({
      code: String(estabelecimento.atividade_principal.id),
      description: toCapital(estabelecimento.atividade_principal.descricao) || '',
      isPrimary: true,
    });
  }

  // Activities - Secondary CNAEs
  if (Array.isArray(estabelecimento.atividades_secundarias)) {
    estabelecimento.atividades_secundarias.forEach(cnae => {
      if (cnae.id) {
        pojo.activities.push({
          code: String(cnae.id),
          description: toCapital(cnae.descricao) || '',
          isPrimary: false,
        });
      }
    });
  }

  // Holders (Sócios)
  pojo.holders = [];
  if (Array.isArray(socios)) {
    socios.forEach(partner => {
      const holder = {
        name: toCapital(partner.nome) || '',
        cpf: cleanPhoneNumber(partner.cpf_cnpj_socio?.replace(/\*/g, '') || ''),
        qualification: toCapital(partner.qualificacao_socio?.descricao) || '',
        qualificationCode: partner.qualificacao_socio?.id || '',
        sinceAt: parseBrazilianDate(partner.data_entrada),
      };

      // Legal representative
      if (partner.nome_representante) {
        holder.legalRepresentative = {
          name: toCapital(partner.nome_representante),
          cpf: cleanPhoneNumber(partner.cpf_representante_legal?.replace(/\*/g, '') || ''),
          qualification: toCapital(partner.qualificacao_representante?.descricao) || '',
        };
      }

      pojo.holders.push(holder);
    });
  }

  // State Registrations (Inscrições Estaduais)
  pojo.stateRegistrations = [];
  if (Array.isArray(estabelecimento.inscricoes_estaduais)) {
    estabelecimento.inscricoes_estaduais.forEach(ie => {
      if (ie.inscricao_estadual) {
        pojo.stateRegistrations.push({
          stateCode: ie.estado?.sigla || '',
          value: ie.inscricao_estadual,
          isActive: ie.ativo === true,
          updatedAt: ie.atualizado_em || null,
        });
      }
    });
  }

  // Fiscal data
  const isSimples = simples.simples === 'Sim';
  const isMei = simples.mei === 'Sim';

  pojo.fiscalData = {
    mainCnae: estabelecimento.atividade_principal?.id
      ? String(estabelecimento.atividade_principal.id)
      : null,
    taxRegime: isSimples ? 'simplesNacional' : null,
    isMei: isMei,
    simplesOptedAt: parseBrazilianDate(simples.data_opcao_simples),
    simplesExcludedAt: parseBrazilianDate(simples.data_exclusao_simples),
  };

  // Registration status
  pojo.registrationStatus = {
    status: mapRegistrationStatus(estabelecimento.situacao_cadastral),
    statusText: toCapital(estabelecimento.situacao_cadastral) || null,
    date: parseBrazilianDate(estabelecimento.data_situacao_cadastral),
    reason: toCapital(estabelecimento.motivo_situacao_cadastral) || null,
    specialStatus: toCapital(estabelecimento.situacao_especial) || null,
    specialStatusDate: parseBrazilianDate(estabelecimento.data_situacao_especial),
  };

  // Other company fields
  pojo.legalNature = toCapital(response.natureza_juridica?.descricao) || null;
  pojo.legalNatureCode = response.natureza_juridica?.id || null;
  pojo.shareCapital = response.capital_social ? parseFloat(response.capital_social) : null;
  pojo.size = toCapital(response.porte?.descricao) || null;

  // Segment - derive from CNAE section code (ISIC Rev.4)
  // CNPJ.ws provides the section letter directly in atividade_principal.secao
  const sectionCode = estabelecimento.atividade_principal?.secao;
  pojo.segment = getIsicSectionFromCnae(sectionCode || estabelecimento.atividade_principal?.id);

  return pojo;
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCnpj,
  normalizeRequestPayload,
  normalizeResponseBody,
};
